import type { AgingReport, BalanceSheet, PnlReport } from '@erp/shared';
import Decimal from 'decimal.js';
import {
  Banknote,
  Coins,
  CreditCard,
  Landmark,
  ReceiptText,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

import { CHART_COLORS, SERIES_PALETTE } from './_shared/chartColors.js';
import { KpiCard } from './_shared/KpiCard.js';
import { PeriodPicker, rangeForPreset, type PeriodValue } from './_shared/PeriodPicker.js';

type TenderKind = 'cash' | 'card' | 'wallet' | 'other';

interface PosDailySummaryRow {
  date: string;
  terminalId: string | null;
  /** Sale currency of this bucket — totals in different currencies never mix. */
  currencyCode: string;
  saleCount: number;
  refundCount: number;
  grossSales: string;
  taxCollected: string;
  byTender: Array<{ kind: TenderKind; currencyCode: string; count: number; total: string }>;
}

export function StatisticsPage(): JSX.Element {
  const { t, i18n } = useTranslation('reports');
  const tenant = useAuthStore((s) => s.tenant);
  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';
  // Profit & loss and the balance sheet are the whole company's finances: only
  // those holding `reports.financial` get them here — the rest of the page (sales,
  // debts, the register) is for anyone who may read reports.
  const canFinancial = usePermission('reports.financial');

  const [period, setPeriod] = useState<PeriodValue>(() => {
    const r = rangeForPreset('last30');
    return { preset: 'last30', from: r.from, to: r.to };
  });
  const [pnl, setPnl] = useState<PnlReport | null>(null);
  const [bs, setBs] = useState<BalanceSheet | null>(null);
  const [ar, setAr] = useState<AgingReport | null>(null);
  const [ap, setAp] = useState<AgingReport | null>(null);
  const [pos, setPos] = useState<PosDailySummaryRow[] | null>(null);
  // Selected POS currency (multi-currency tenants): KPI, trend + tender pie all
  // reflect ONLY this currency. null = follow the default (base if present).
  const [posCurrency, setPosCurrency] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPnl(null); setBs(null); setAr(null); setAp(null); setPos(null); setPosCurrency(null);
    (async () => {
      try {
        const [pnlR, bsR, arR, apR, posR] = await Promise.all([
          canFinancial
            ? api().get<PnlReport>(`/reports/profit-and-loss?from=${period.from}&to=${period.to}`)
            : Promise.resolve(null),
          canFinancial
            ? api().get<BalanceSheet>(`/reports/balance-sheet?asOf=${period.to}`)
            : Promise.resolve(null),
          api().get<AgingReport>(`/reports/ar-aging?asOf=${period.to}`),
          api().get<AgingReport>(`/reports/ap-aging?asOf=${period.to}`),
          api().get<{ items: PosDailySummaryRow[] }>(
            `/pos/reports/daily-summary?from=${period.from}&to=${period.to}`,
          ).catch(() => ({ data: { items: [] as PosDailySummaryRow[] } })),
        ]);
        if (cancelled) return;
        setPnl(pnlR?.data ?? null);
        setBs(bsR?.data ?? null);
        setAr(arR.data);
        setAp(apR.data);
        setPos(posR.data.items);
      } catch {
        // Each card handles its own loading state. A top-level toast is
        // overkill here — the empty-state copy explains what to do.
      }
    })();
    return () => { cancelled = true; };
  }, [period.from, period.to, canFinancial]);

  const fmt = (amt: string): string =>
    formatMoney(amt, { locale: i18n.language, currency: baseCurrency });
  // Shorter formatter for KPI deltas and chart axes — no currency symbol so
  // the numbers don't fight for space.
  const fmtShort = (amt: number): string =>
    formatNumber(amt, { locale: i18n.language }, {
      notation: amt >= 10_000 ? 'compact' : 'standard',
      maximumFractionDigits: amt >= 10_000 ? 1 : 0,
    });

  // --- derived metrics -------------------------------------------------------

  // Distinct POS currencies in the window; the KPI/trend/tender pie all scope to
  // one of them. Default to the tenant base if it's present, else the first.
  const posCurrencies = useMemo(
    () => (pos ? [...new Set(pos.map((d) => d.currencyCode))] : []),
    [pos],
  );
  const defaultPosCurrency = posCurrencies.includes(baseCurrency) ? baseCurrency : posCurrencies[0] ?? null;
  const activePosCurrency = posCurrency && posCurrencies.includes(posCurrency) ? posCurrency : defaultPosCurrency;
  // POS figures are in the selected sale currency, NOT necessarily the base.
  const fmtPos = (amt: string): string =>
    formatMoney(amt, { locale: i18n.language, currency: activePosCurrency ?? baseCurrency });

  const posTotals = useMemo(() => {
    if (!pos) return null;
    let gross = new Decimal(0);
    let count = 0;
    for (const d of pos) {
      if (d.currencyCode !== activePosCurrency) continue;
      gross = gross.plus(d.grossSales);
      count += d.saleCount + d.refundCount;
    }
    return { gross: gross.toFixed(2), count };
  }, [pos, activePosCurrency]);

  const trendData = useMemo(() => {
    if (!pos) return null;
    // Backfill missing days inside [from, to] with zeros so the area chart
    // has a continuous baseline, otherwise sparse activity creates a jumpy
    // shape that misrepresents the trend.
    const map = new Map<string, number>();
    for (const d of pos) {
      if (d.currencyCode !== activePosCurrency) continue;
      map.set(d.date, (map.get(d.date) ?? 0) + Number(d.grossSales));
    }
    const out: Array<{ date: string; gross: number }> = [];
    const start = new Date(`${period.from}T00:00:00Z`);
    const end = new Date(`${period.to}T00:00:00Z`);
    for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 86_400_000)) {
      const iso = d.toISOString().slice(0, 10);
      out.push({ date: iso, gross: map.get(iso) ?? 0 });
    }
    return out;
  }, [pos, period.from, period.to, activePosCurrency]);

  const tenderSeries = useMemo(() => {
    if (!pos) return null;
    const totals: Record<TenderKind, number> = { cash: 0, card: 0, wallet: 0, other: 0 };
    for (const d of pos) {
      if (d.currencyCode !== activePosCurrency) continue;
      for (const tr of d.byTender) {
        totals[tr.kind] += Number(tr.total);
      }
    }
    return (['cash', 'card', 'wallet', 'other'] as TenderKind[])
      .map((k) => ({ name: k, value: totals[k] }))
      .filter((s) => s.value > 0);
  }, [pos, activePosCurrency]);

  // The dashboard is denominated in the base currency, so scope the AR widgets
  // to the base-currency slice of the per-currency totals (the legacy `totals`
  // field sums across currencies, which is meaningless to label as one figure).
  const arBase = useMemo(
    () => ar?.totalsByCurrency.find((x) => x.currencyCode === baseCurrency) ?? null,
    [ar, baseCurrency],
  );

  const agingBuckets = useMemo(() => {
    if (!ar) return null;
    return [
      { bucket: 'current', value: Number(arBase?.current ?? 0), color: CHART_COLORS.success },
      { bucket: 'days_1_30', value: Number(arBase?.days_1_30 ?? 0), color: CHART_COLORS.info },
      { bucket: 'days_31_60', value: Number(arBase?.days_31_60 ?? 0), color: CHART_COLORS.warning },
      { bucket: 'days_61_90', value: Number(arBase?.days_61_90 ?? 0), color: CHART_COLORS.warning },
      { bucket: 'days_90_plus', value: Number(arBase?.days_90_plus ?? 0), color: CHART_COLORS.danger },
    ];
  }, [ar, arBase]);

  const topCustomers = useMemo(() => {
    if (!ar) return null;
    // A party can now appear once per currency — keep only base-currency rows so
    // the "top by AR" ranking compares like-for-like (all in the base currency).
    return ar.rows
      .filter((r) => r.currencyCode === baseCurrency)
      .map((r) => ({ ...r, totalN: Number(r.total) }))
      .sort((a, b) => b.totalN - a.totalN)
      .slice(0, 5);
  }, [ar, baseCurrency]);

  const netMargin = useMemo(() => {
    if (!pnl) return null;
    const rev = new Decimal(pnl.totalRevenue);
    if (rev.lte(0)) return null;
    return rev.minus(pnl.totalExpenses).dividedBy(rev).times(100).toFixed(1);
  }, [pnl]);

  const overduePercent = useMemo(() => {
    if (!arBase) return null;
    const tot = new Decimal(arBase.total);
    if (tot.lte(0)) return null;
    const overdue = new Decimal(arBase.days_1_30)
      .plus(arBase.days_31_60)
      .plus(arBase.days_61_90)
      .plus(arBase.days_90_plus);
    return overdue.dividedBy(tot).times(100).toFixed(1);
  }, [arBase]);

  // --- render ---------------------------------------------------------------

  return (
    <div className="flex flex-col gap-4" lang={i18n.language}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('statistics.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('statistics.description')}</p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </header>

      {/* ---------- KPI strip ---------- */}
      <div className={'grid grid-cols-2 gap-3 ' + (canFinancial ? 'lg:grid-cols-5' : 'lg:grid-cols-2')}>
        {canFinancial && (
          <>
            <KpiCard
              tone="success"
              icon={TrendingUp}
              label={t('statistics.kpi.revenue')}
              value={pnl ? fmt(pnl.totalRevenue) : '—'}
              loading={!pnl}
            />
            <KpiCard
              tone="warning"
              icon={TrendingDown}
              label={t('statistics.kpi.expenses')}
              value={pnl ? fmt(pnl.totalExpenses) : '—'}
              loading={!pnl}
            />
            <KpiCard
              tone={pnl && Number(pnl.netIncome) >= 0 ? 'brand' : 'danger'}
              icon={Landmark}
              label={t('statistics.kpi.netIncome')}
              value={pnl ? fmt(pnl.netIncome) : '—'}
              hint={netMargin ? t('pnl.marginPercent') + ` ${netMargin}%` : undefined}
              loading={!pnl}
            />
          </>
        )}
        <KpiCard
          tone="info"
          icon={Coins}
          label={t('statistics.kpi.arOutstanding')}
          value={ar ? fmt(arBase?.total ?? '0') : '—'}
          hint={overduePercent ? `${overduePercent}% ${t('aging.overdue')}` : undefined}
          loading={!ar}
        />
        <KpiCard
          tone="brand"
          icon={ReceiptText}
          label={t('statistics.kpi.posSales')}
          value={posTotals ? fmtPos(posTotals.gross) : '—'}
          hint={posTotals ? `${posTotals.count} ${t('statistics.kpi.posTransactions').toLowerCase()}` : undefined}
          loading={!pos}
        />
      </div>

      {/* ---------- POS trend + tenders ---------- */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr]">
        <Card className="p-4">
          <SectionHeading title={t('statistics.sections.posTrend')} to="/pos/sales" />
          {posCurrencies.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label={t('statistics.currencyFilter', { defaultValue: 'Filter by currency' })}>
              {posCurrencies.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setPosCurrency(c)}
                  aria-pressed={c === activePosCurrency}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium tabular-nums transition ${
                    c === activePosCurrency
                      ? 'border-brand bg-brand/10 text-brand'
                      : 'border-border text-fg-muted hover:bg-bg-subtle'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          {!trendData ? (
            <Skeleton className="mt-3 h-64 w-full" />
          ) : trendData.every((d) => d.gross === 0) ? (
            <EmptyChart text={t('statistics.noData')} />
          ) : (
            <div className="mt-2 h-64 w-full" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gross-gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.brand} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={CHART_COLORS.brand} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }}
                    tickFormatter={(d: string) => d.slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }}
                    tickFormatter={(v: number) => fmtShort(v)}
                    width={56}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle()}
                    formatter={(v) => [fmtPos(String(v ?? 0)), t('statistics.kpi.posSales')]}
                    labelFormatter={(d) => String(d ?? '')}
                  />
                  <Area
                    type="monotone"
                    dataKey="gross"
                    stroke={CHART_COLORS.brand}
                    strokeWidth={2}
                    fill="url(#gross-gradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <SectionHeading title={t('statistics.sections.tenders')} />
          {!tenderSeries ? (
            <Skeleton className="mt-3 h-64 w-full" />
          ) : tenderSeries.length === 0 ? (
            <EmptyChart text={t('statistics.noData')} />
          ) : (
            <div className="mt-2 grid grid-cols-[1fr_auto] items-center gap-3">
              <div className="h-48" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={tenderSeries}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={42}
                      outerRadius={70}
                      paddingAngle={2}
                      stroke={CHART_COLORS.bg}
                      strokeWidth={2}
                    >
                      {tenderSeries.map((entry, i) => (
                        <Cell key={entry.name} fill={SERIES_PALETTE[i % SERIES_PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={tooltipStyle()}
                      formatter={(v) => fmtPos(String(v ?? 0))}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="flex flex-col gap-1 text-xs">
                {tenderSeries.map((s, i) => (
                  <li key={s.name} className="flex items-center gap-2 whitespace-nowrap">
                    <TenderDot color={SERIES_PALETTE[i % SERIES_PALETTE.length]!} />
                    <TenderIcon kind={s.name} />
                    <span className="font-medium">{tenderLabel(s.name)}</span>
                    <span className="ms-auto font-mono tabular-nums">{fmtPos(String(s.value))}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      {/* ---------- AR aging buckets + top customers ---------- */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr]">
        <Card className="p-4">
          <SectionHeading title={t('statistics.sections.aging')} to="/reports/ar-aging" />
          {!agingBuckets ? (
            <Skeleton className="mt-3 h-56 w-full" />
          ) : agingBuckets.every((b) => b.value === 0) ? (
            <EmptyChart text={t('aging.empty')} />
          ) : (
            <div className="mt-2 h-56 w-full" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingBuckets} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="bucket"
                    tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }}
                    tickFormatter={(b: string) => t(`aging.columns.${b}`)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }}
                    tickFormatter={(v: number) => fmtShort(v)}
                    width={56}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle()}
                    formatter={(v) => [fmt(String(v ?? 0)), t('aging.outstanding')]}
                    labelFormatter={(b) => t(`aging.columns.${String(b ?? '')}`)}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {agingBuckets.map((b) => (
                      <Cell key={b.bucket} fill={b.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <SectionHeading title={t('statistics.sections.topCustomers')} to="/reports/ar-aging" />
          {!topCustomers ? (
            <Skeleton className="mt-3 h-48 w-full" />
          ) : topCustomers.length === 0 ? (
            <EmptyChart text={t('aging.empty')} />
          ) : (
            <ul className="mt-2 flex flex-col divide-y divide-border-subtle">
              {topCustomers.map((c) => {
                const total = c.totalN;
                const overdue = Number(c.days_1_30) + Number(c.days_31_60)
                  + Number(c.days_61_90) + Number(c.days_90_plus);
                const overduePct = total > 0 ? Math.min(100, (overdue / total) * 100) : 0;
                return (
                  <li key={c.partyId} className="flex items-center gap-3 py-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{c.partyName}</div>
                      <div className="text-xs text-fg-muted">{c.partyCode}</div>
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle">
                        <div
                          className="h-full rounded-full bg-danger/70"
                          style={{ width: `${overduePct}%` }}
                          aria-hidden
                        />
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="font-mono text-sm tabular-nums">{fmt(c.total)}</div>
                      {overdue > 0 && (
                        <div className="text-xs text-danger">
                          {overduePct.toFixed(0)}% {t('aging.overdue')}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {/* ---------- P&L mini + Balance sheet snapshot ---------- */}
      {canFinancial && (
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card className="p-4">
          <SectionHeading title={t('statistics.sections.pnlSummary')} to="/reports/profit-and-loss" />
          {!pnl ? (
            <Skeleton className="mt-3 h-40 w-full" />
          ) : (
            <PnlMini pnl={pnl} fmt={fmt} />
          )}
        </Card>
        <Card className="p-4">
          <SectionHeading title={t('statistics.sections.balanceSnapshot')} to="/reports/balance-sheet" />
          {!bs ? (
            <Skeleton className="mt-3 h-40 w-full" />
          ) : (
            <BalanceMini bs={bs} apOutstanding={ap?.totalsByCurrency.find((x) => x.currencyCode === baseCurrency)?.total ?? null} fmt={fmt} />
          )}
        </Card>
      </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers + sub-components

function SectionHeading({ title, to }: { title: string; to?: string }): JSX.Element {
  const { t } = useTranslation('reports');
  return (
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
        {title}
      </h2>
      {to && (
        <Link to={to} className="text-xs text-brand underline-offset-4 hover:underline">
          {t('statistics.viewFullReport')} →
        </Link>
      )}
    </div>
  );
}

function EmptyChart({ text }: { text: string }): JSX.Element {
  return (
    <div className="mt-3 flex h-48 items-center justify-center rounded-md border border-dashed border-border bg-bg-subtle/30 text-sm text-fg-muted">
      {text}
    </div>
  );
}

function tooltipStyle(): React.CSSProperties {
  return {
    background: CHART_COLORS.bg,
    border: `1px solid ${CHART_COLORS.border}`,
    borderRadius: 6,
    fontSize: 12,
    color: CHART_COLORS.fg,
  };
}

function TenderDot({ color }: { color: string }): JSX.Element {
  return <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />;
}

function TenderIcon({ kind }: { kind: TenderKind }): JSX.Element {
  switch (kind) {
    case 'cash': return <Banknote className="h-3.5 w-3.5" aria-hidden />;
    case 'card': return <CreditCard className="h-3.5 w-3.5" aria-hidden />;
    case 'wallet': return <Wallet className="h-3.5 w-3.5" aria-hidden />;
    default: return <CreditCard className="h-3.5 w-3.5" aria-hidden />;
  }
}

function tenderLabel(k: TenderKind): string {
  return k.charAt(0).toUpperCase() + k.slice(1);
}

interface PnlMiniProps {
  pnl: PnlReport;
  fmt: (s: string) => string;
}

function PnlMini({ pnl, fmt }: PnlMiniProps): JSX.Element {
  const { t } = useTranslation('reports');
  const rev = new Decimal(pnl.totalRevenue);
  const exp = new Decimal(pnl.totalExpenses);
  const net = new Decimal(pnl.netIncome);
  const max = Decimal.max(rev.abs(), exp.abs(), new Decimal(1));
  const bar = (v: Decimal): string => `${Math.min(100, v.abs().dividedBy(max).times(100).toNumber()).toFixed(1)}%`;
  return (
    <dl className="mt-2 flex flex-col gap-3 text-sm">
      <Row label={t('pnl.totalRevenue')} amount={fmt(pnl.totalRevenue)} barWidth={bar(rev)} barClass="bg-success/70" />
      <Row label={t('pnl.totalExpenses')} amount={fmt(pnl.totalExpenses)} barWidth={bar(exp)} barClass="bg-warning/70" />
      <div className="mt-1 flex items-center justify-between border-t border-border pt-3">
        <span className="font-semibold">{t('pnl.netIncome')}</span>
        <span
          className={`font-mono text-lg font-semibold tabular-nums ${net.gte(0) ? 'text-success' : 'text-danger'}`}
        >
          {fmt(pnl.netIncome)}
        </span>
      </div>
    </dl>
  );
}

function Row({
  label, amount, barWidth, barClass,
}: { label: string; amount: string; barWidth: string; barClass: string }): JSX.Element {
  return (
    <div>
      <div className="flex justify-between">
        <span className="text-fg-muted">{label}</span>
        <span className="font-mono tabular-nums">{amount}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: barWidth }} aria-hidden />
      </div>
    </div>
  );
}

interface BalanceMiniProps {
  bs: BalanceSheet;
  /** Base-currency AP outstanding (per-currency slice), or null when none. */
  apOutstanding: string | null;
  fmt: (s: string) => string;
}

function BalanceMini({ bs, apOutstanding, fmt }: BalanceMiniProps): JSX.Element {
  const { t } = useTranslation('reports');
  const a = new Decimal(bs.totalAssets);
  const l = new Decimal(bs.totalLiabilities);
  const e = new Decimal(bs.totalEquity);
  const sumLE = l.plus(e);
  const diff = a.minus(sumLE);
  const balanced = diff.abs().lt(0.005);
  return (
    <div className="mt-2 flex flex-col gap-3 text-sm">
      <dl className="grid grid-cols-[1fr_auto] gap-y-1">
        <dt className="text-fg-muted">{t('balanceSheet.totalAssets')}</dt>
        <dd className="text-end font-mono tabular-nums">{fmt(bs.totalAssets)}</dd>
        <dt className="text-fg-muted">{t('balanceSheet.totalLiabilities')}</dt>
        <dd className="text-end font-mono tabular-nums">{fmt(bs.totalLiabilities)}</dd>
        <dt className="text-fg-muted">{t('balanceSheet.totalEquity')}</dt>
        <dd className="text-end font-mono tabular-nums">{fmt(bs.totalEquity)}</dd>
      </dl>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <span className="font-semibold">{t('balanceSheet.balances')}</span>
        {balanced ? (
          <Badge variant="success">{t('balanceSheet.balanced')}</Badge>
        ) : (
          <Badge variant="warning">{t('balanceSheet.imbalance', { amount: fmt(diff.toFixed(4)) })}</Badge>
        )}
      </div>
      {apOutstanding != null && Number(apOutstanding) > 0 && (
        <div className="rounded-md border border-border-subtle bg-bg-subtle/30 p-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-medium text-fg-muted">{t('statistics.kpi.apOutstanding')}</span>
            <span className="font-mono font-semibold tabular-nums">{fmt(apOutstanding)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
